import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import toast from "../../../utils/toast";

import SimpleReactValidator from "simple-react-validator";

import {
  postLoginData,
  setSessionData,
  setLocalStorageData,
} from "../../../services";
import { RECEIVE_LOGIN_DETAIL } from "../../../constants/ActionTypes";
import store from "../../../store";
import { getUserProfileData, mergeCartFromServer } from "../../../actions";

import Seo from "../../layouts/common/seo";
import Breadcrumb from "../../new_template_common/breadcrumb/BreadCrumb";

import ButtonSpinner from "../../common/ButtonSpinner";
import PasswordInput from "../../common/PasswordInput/PasswordInput";
import classes from "./checkout-auth.module.scss";

const CheckoutAuth = (props) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  const validator = useRef(new SimpleReactValidator({ autoForceUpdate: this }));
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const isAuthenticated =
    props.user_type === "CUSTOMER" ||
    localStorage.getItem("user_type") === "CUSTOMER";

  // Redirect to checkout if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      props.history.replace(`${process.env.PUBLIC_URL}/checkout`);
    }
  }, [isAuthenticated, props.history]);

  // Pre-fill saved credentials
  useEffect(() => {
    if (localStorage.getItem("email") && localStorage.getItem("password")) {
      setEmail(localStorage.getItem("email"));
      setPassword(localStorage.getItem("password"));
    }
  }, []);

  // Email login handler
  const handleEmailLogin = (e) => {
    e.preventDefault();
    if (validator.current.allValid()) {
      setLoginLoading(true);
      setLoginError("");

      if (rememberMe) {
        localStorage.setItem("email", email);
        localStorage.setItem("password", password);
      }

      const POST = {
        email: email,
        password: window.btoa(password),
      };

      postLoginData(POST)
        .then(async (response) => {
          setLoginLoading(false);
          if (response.data && response.data.success === true) {
            setSessionData(response.data.result);
            setLocalStorageData(response.data.result);
            toast.success("Logged in successfully!", {
              toastId: "checkout-login-success",
              hideProgressBar: true,
            });
            store.dispatch({
              type: RECEIVE_LOGIN_DETAIL,
              payload: response.data.result,
            });
            // Merge any guest cart into this user, then load the unified cart.
            store.dispatch(mergeCartFromServer());
            await store.dispatch(getUserProfileData());
            props.history.replace(`${process.env.PUBLIC_URL}/checkout`);
          } else {
            setLoginError("Invalid email or password. Please try again.");
          }
        })
        .catch(() => {
          setLoginLoading(false);
          setLoginError("Login failed. Please try again.");
        });
    } else {
      validator.current.showMessages();
      forceUpdate();
    }
  };

  const handleContinueAsGuest = () => {
    props.history.replace(`${process.env.PUBLIC_URL}/checkout`);
  };

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className={`site-main`}>
      <div className={classes.auth_page}>
        <Seo type="checkout-head" />
        <div className="custom-container">
          <div className={classes.auth_container}>
            <Breadcrumb current="Checkout" />

            <div className={classes.auth_card}>
              <div className={classes.header_wrapper}>
                <h3 className={`${classes.main_title} m-0`}>Welcome Back</h3>
                <p className={classes.sub_title}>
                  Sign in to continue to your account.
                </p>
              </div>

              <form
                className={`${classes.auth_form} uk-form-stacked`}
                onSubmit={handleEmailLogin}
              >
                <div className={classes.form_group}>
                  <div className={classes.form_control}>
                    <input
                      type="email"
                      id="checkout-email"
                      placeholder=" "
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => validator.current.showMessageFor("email")}
                    />
                    <label
                      className={classes.floating_label}
                      htmlFor="checkout-email"
                    >
                      Email<span className="required-asterisk">*</span>
                    </label>
                  </div>
                  {validator.current.message("email", email, "required|email", {
                    messages: {
                      required: "Enter your email.",
                      email: "Enter a valid email address.",
                    },
                  })}
                </div>

                <PasswordInput
                  name="password"
                  id="checkout-password"
                  placeholder=" "
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  floatingLabel
                  label={<>Password<span className="required-asterisk">*</span></>}
                  className={classes.form_group}
                  wrapperClassName={`${classes.form_control} ${classes.password}`}
                  inputClassName=""
                  labelClassName={classes.floating_label}
                  error={validator.current.message("password", password, "required", {
                    messages: { required: "Enter your password." },
                  })}
                />

                <div className={classes.form_action}>
                  <div className={`${classes.checkbox} uk-form-controls`}>
                    <label
                      className="uk-form-label para-one m-0"
                      htmlFor="checkout-rememberMe"
                    >
                      <input
                        className="uk-checkbox"
                        type="checkbox"
                        id="checkout-rememberMe"
                        name="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <span>Remember me</span>
                    </label>
                  </div>
                  <div className={classes.forgot_password}>
                    <Link to={`${process.env.PUBLIC_URL}/forgot-password`}>
                      Forgot Password?
                    </Link>
                  </div>
                </div>

                {loginError && (
                  <div className={classes.error_box}>
                    <p>{loginError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  className={classes.sign_in_btn}
                  disabled={loginLoading}
                >
                  {loginLoading ? <ButtonSpinner /> : "SIGN IN"}
                </button>
              </form>

              <p className={classes.signup_text}>
                Don't have an account?{" "}
                <Link to={`${process.env.PUBLIC_URL}/create?ref=checkout`}>Sign up</Link>
              </p>

              <div className={classes.divider}>
                <span>or</span>
              </div>

              <button
                type="button"
                className={classes.guest_btn}
                onClick={handleContinueAsGuest}
              >
                CONTINUE AS GUEST
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user_type: state.data.user_type,
});

export default connect(mapStateToProps)(CheckoutAuth);
